import { useState, useEffect } from "react";
import api, { formatCurrency } from "@/lib/api";
import { CreditCard, Check, ArrowRight, AlertTriangle, Clock } from "lucide-react";

export default function SubscriptionPage() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: s }, { data: p }] = await Promise.all([
          api.get("/subscription/status"),
          api.get("/subscription/plans")
        ]);
        setSub(s);
        setPlans(p);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const subscribe = async (plan) => {
    setProcessing(true);
    try {
      const { data } = await api.post("/subscription/initialize", { plan });
      // Redirect to Paystack checkout
      window.location.href = data.authorization_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Error initializing payment");
    } finally {
      setProcessing(false);
    }
  };

  // Check for subscription success callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("reference") || params.get("trxref");
    if (ref) {
      (async () => {
        try {
          const { data } = await api.post("/subscription/verify", { reference: ref });
          if (data.status === "success") {
            alert(`Subscription activated! Plan: ${data.subscription.plan}`);
            window.history.replaceState({}, "", "/subscription");
            const { data: s } = await api.get("/subscription/status");
            setSub(s);
          }
        } catch (e) { console.error(e); }
      })();
    }
  }, []);

  if (loading) return <div className="stratum-card p-12 text-center text-[#6B7280]">Loading...</div>;

  const trialDays = sub?.trial_ends ? Math.max(0, Math.ceil((new Date(sub.trial_ends) - new Date()) / (1000 * 60 * 60 * 24))) : 90;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
          <CreditCard size={24} className="inline mr-2 text-[#C8A961]" />
          Subscription
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">Manage your plan and billing</p>
      </div>

      {/* Current Status */}
      <div className={`stratum-card p-6 ${sub?.status === "active" ? "border-l-4 border-l-[#065F46]" : sub?.status === "expired" ? "border-l-4 border-l-[#991B1B]" : "border-l-4 border-l-[#C8A961]"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-display text-xl font-semibold text-[#0B1020]">{sub?.plan_name || "Free"} Plan</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                sub?.status === "active" ? "bg-[#065F46]/10 text-[#065F46]" :
                sub?.status === "expired" ? "bg-[#991B1B]/10 text-[#991B1B]" :
                "bg-[#C8A961]/10 text-[#C8A961]"
              }`}>{sub?.status === "trialing" ? "Trial" : sub?.status === "active" ? "Active" : "Expired"}</span>
            </div>
            {sub?.status === "trialing" && (
              <p className="text-sm text-[#6B7280] mt-1">
                <Clock size={14} className="inline mr-1" />
                {trialDays} days remaining in your free trial
              </p>
            )}
            {sub?.status === "expired" && (
              <p className="text-sm text-[#991B1B] mt-1">
                <AlertTriangle size={14} className="inline mr-1" />
                Your trial has expired. Subscribe to continue using Stratum. Data will be deleted after 5 months of inactivity.
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="font-mono text-2xl font-semibold text-[#0B1020] tabular-nums">{sub?.amount_display}</div>
            {sub?.plan !== "free" && sub?.status === "active" && <div className="text-xs text-[#6B7280]">per month</div>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
          <div className="p-3 rounded-lg bg-[#FAF8F4]">
            <div className="text-xs text-[#6B7280]">Entities</div>
            <div className="font-mono tabular-nums">{sub?.entity_count} / {sub?.entity_limit === 999999 ? "Unlimited" : sub?.entity_limit}</div>
          </div>
          <div className="p-3 rounded-lg bg-[#FAF8F4]">
            <div className="text-xs text-[#6B7280]">Assets</div>
            <div className="font-mono tabular-nums">{sub?.asset_count} / {sub?.asset_limit === 999999 ? "Unlimited" : sub?.asset_limit}</div>
          </div>
        </div>
      </div>

      {/* Plan Grid */}
      <div>
        <h3 className="font-display text-lg font-semibold text-[#0B1020] mb-4">Choose Your Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(plans).filter(([k]) => k !== "free").map(([key, plan]) => {
            const isCurrent = sub?.plan === key;
            return (
              <div key={key} className={`stratum-card p-5 flex flex-col ${isCurrent ? "ring-2 ring-[#065F46]" : ""} ${key === "group" ? "ring-2 ring-[#C8A961]/30" : ""}`} data-testid={`plan-${key}`}>
                {key === "group" && <div className="text-[9px] uppercase tracking-widest text-[#C8A961] font-medium mb-1">Most Popular</div>}
                <h4 className="font-display text-lg font-semibold text-[#0B1020]">{plan.name}</h4>
                <div className="mt-1 mb-3">
                  <span className="font-display text-2xl font-bold text-[#0B1020]">{plan.amount_display}</span>
                </div>
                <ul className="flex-1 space-y-1.5 mb-4 text-xs text-[#6B7280]">
                  <li className="flex items-center gap-1.5"><Check size={10} className="text-[#065F46]" />{plan.entity_limit === 999999 ? "Unlimited" : `Up to ${plan.entity_limit}`} entities</li>
                  <li className="flex items-center gap-1.5"><Check size={10} className="text-[#065F46]" />{plan.asset_limit === 999999 ? "Unlimited" : `Up to ${plan.asset_limit}`} assets</li>
                </ul>
                {isCurrent ? (
                  <span className="text-center py-2.5 rounded-lg bg-[#065F46]/10 text-[#065F46] text-sm font-medium">Current Plan</span>
                ) : key === "enterprise" ? (
                  <a href="mailto:info@stratumassetreg.com?subject=Enterprise%20Enquiry" className="text-center py-2.5 rounded-lg border border-[#E8E2D5] text-sm font-medium hover:bg-[#FAF8F4] flex items-center justify-center gap-1">
                    Contact Sales <ArrowRight size={12} />
                  </a>
                ) : (
                  <button onClick={() => subscribe(key)} disabled={processing}
                    className={`text-center py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${key === "group" ? "bg-[#C8A961] text-[#0B1020] hover:bg-[#B69A51]" : "bg-[#0B1020] text-white hover:bg-[#1A233A] hover:text-[#C8A961]"}`}
                    data-testid={`subscribe-${key}`}>
                    {processing ? "Processing..." : "Subscribe Now"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-[#6B7280] text-center">
        Payments processed securely by Paystack. All amounts in ZAR. Cancel any time.
        <br />After 5 months of inactivity without a subscription, all data and your profile will be permanently deleted.
      </div>
    </div>
  );
}
